import { forwardRef, useState } from 'react'
import { IMaskInput, IMask } from 'react-imask'
import { Button, Grid, TextField, Typography, styled, Card } from '@mui/material'
import { Box } from '@mui/system'
import { luhnCheck, validateCPF, validateCNPJ } from '../../../utils/common'
import { LoadingButton } from '@mui/lab'

const CommonCard = styled(Card)(({ theme }) => ({
  boxShadow: theme.customShadows.zCard,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: 'transparent',
}))

const CreditMask = forwardRef(function CreditMask(props, ref) {
  const { onChange, ...other } = props
  return (
    <IMaskInput
      {...other}
      mask="0000 0000 0000 0000"
      inputRef={ref}
      onAccept={value => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

const CepMask = forwardRef(function CepMask(props, ref) {
  const { onChange, ...other } = props
  return (
    <IMaskInput
      {...other}
      mask="00000-000"
      inputRef={ref}
      onAccept={value => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

const ValidadeMask = forwardRef(function ValidadeMask(props, ref) {
  const { onChange, ...other } = props
  return (
    <IMaskInput
      {...other}
      mask="MM/AA"
      blocks={{
        MM: {
          mask: IMask.MaskedRange,
          placeholderChar: 'M',
          from: 1,
          to: 12,
          maxLength: 2,
        },
        AA: {
          mask: IMask.MaskedRange,
          placeholderChar: 'A',
          from: 1,
          to: 99,
          maxLength: 2,
        },
      }}
      inputRef={ref}
      onAccept={value => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

const CodigoMask = forwardRef(function CodigoMask(props, ref) {
  const { onChange, ...other } = props
  return (
    <IMaskInput
      {...other}
      mask="000"
      inputRef={ref}
      onAccept={value => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

const CPFMask = forwardRef(function CPFMask(props, ref) {
  const { onChange, ...other } = props
  return (
    <IMaskInput
      {...other}
      mask={props.value.length > 14 ? "00.000.000/0000-00" : "000.000.000-000"}
      inputRef={ref}
      onAccept={value => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  )
})

export default function PaymentCard({ onSubmit, isLoadingButton }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = () => {
    let errObj = null
    if (!data?.credit) {
      errObj = {
        ...errObj,
        credit: `Número do cartão de crédito é obrigatório!`,
      }
    } else if (data?.credit.length !== 19) {
      errObj = {
        ...errObj,
        credit: `Formato do número do cartão de crédito inválido!`,
      }
    } else {
      if (!luhnCheck(data?.credit.replace(/\s/g, ''))) {
        errObj = {
          ...errObj,
          credit: `Número de cartão inválido!`,
        }
      }
    }

    if (!data?.name) {
      errObj = {
        ...errObj,
        name: `Nome impresso no cartão é obrigatório!`,
      }
    }
    if (!data?.validade) {
      errObj = {
        ...errObj,
        validade: `Validade é obrigatória!`,
      }
    } else if (data?.validade.length !== 5) {
      errObj = {
        ...errObj,
        validade: `Formato da validade inválido!`,
      }
    }
    if (!data?.codigo) {
      errObj = {
        ...errObj,
        codigo: `Código de segurança é obrigatório!`,
      }
    } else if (data?.codigo.length !== 3) {
      errObj = {
        ...errObj,
        codigo: `Formato do código de segurança inválido!`,
      }
    }
    if (!data?.cpf) {
      errObj = {
        ...errObj,
        cpf: `CPF é obrigatório!`,
      }
    } else if (data?.cpf.length !== 14 && data?.cpf.length !== 18) {
      errObj = {
        ...errObj,
        cpf: `Formato do cpf ou cnpj inválido!`,
      }
    } else {
      if (data?.cpf.length === 14) {
        if (!validateCPF(data?.cpf)) {
          errObj = {
            ...errObj,
            cpf: `CPF inválido!`,
          }
        }
      } else {
        if (!validateCNPJ(data?.cpf)) {
          errObj = {
            ...errObj,
            cpf: `CNPJ inválido!`,
          }
        }
      }
    }

    if (!data?.cep) {
      errObj = {
        ...errObj,
        cep: `CEP é obrigatório!`,
      }
    } else if (data?.cep.length !== 9) {
      errObj = {
        ...errObj,
        cep: `Formato de CEP inválido!`,
      }
    }

    if (!data?.address) {
      errObj = {
        ...errObj,
        address: `Endereço é obrigatório!`,
      }
    }

    if (!data?.number) {
      errObj = {
        ...errObj,
        number: `Número é obrigatório!`,
      }
    }

    if (!data?.bairro) {
      errObj = {
        ...errObj,
        bairro: `Bairro é obrigatório!`,
      }
    }

    if (!data?.cidade) {
      errObj = {
        ...errObj,
        cidade: `Cidade / Estado é obrigatório!`,
      }
    }

    if (errObj) {
      setError(errObj)
      return
    }

    onSubmit(data)
  }

  const updateData = (key, val) => {
    setError(null)
    const updated = { ...data }
    updated[key] = val
    setData(updated)
  }

  const getInfosCEP = async (i) => {
    var cep = data?.cep.replace(/\D/g, '');
    var result = await fetch('https://viacep.com.br/ws/' + cep + '/json');
    result = await result.json()
    if (result.erro) {
      setError({
        cep: 'CEP inválido, por favor digite outro!'
      })
    } else {
      const updated = { ...data }
      updated.cidade = result.localidade + '/' + result.uf
      updated.address = result.logradouro
      updated.bairro = result.bairro
      setData(updated)
    }
  }

  return (
    <CommonCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <TextField
          label="Número do Cartão de Crédito *"
          variant="outlined"
          fullWidth
          value={data?.credit || ''}
          onChange={e => updateData('credit', e.target.value)}
          InputProps={{ inputComponent: CreditMask }}
          error={error?.credit ? true : false}
          helperText={error?.credit || ''}
        />
        <TextField
          label="Nome Impresso no Cartão *"
          variant="outlined"
          fullWidth
          value={data?.name || ''}
          onChange={e => updateData('name', e.target.value)}
          error={error?.name ? true : false}
          helperText={error?.name || ''}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Validade *"
              variant="outlined"
              fullWidth
              value={data?.validade || ''}
              // ref={validadeRef}
              InputProps={{ inputComponent: ValidadeMask }}
              onChange={e => updateData('validade', e.target.value)}
              error={error?.validade ? true : false}
              helperText={error?.validade || ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Código de Segurança *"
              variant="outlined"
              fullWidth
              value={data?.codigo || ''}
              InputProps={{ inputComponent: CodigoMask }}
              onChange={e => updateData('codigo', e.target.value)}
              error={error?.codigo ? true : false}
              helperText={error?.codigo || ''}
            />
          </Grid>
        </Grid>
        <TextField
          label="CPF ou CNPJ do Titular *"
          variant="outlined"
          fullWidth
          value={data?.cpf || ''}
          InputProps={{ inputComponent: CPFMask }}
          onChange={e => updateData('cpf', e.target.value)}
          error={error?.cpf ? true : false}
          helperText={error?.cpf || ''}
        />

        <Typography variant="subtitle2" color="text.secondary">
          Endereço de Cobrança
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="CEP *"
              variant="outlined"
              fullWidth
              value={data?.cep || ''}
              onChange={e => updateData('cep', e.target.value)}
              InputProps={{ inputComponent: CepMask }}
              error={error?.cep ? true : false}
              helperText={error?.cep || ''}
            />
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="text" disabled={!(data?.cep) || data?.cep.length < 9} onClick={() => getInfosCEP()}>Buscar</Button>
          </Grid>
        </Grid>
        <TextField
          label="Endereço *"
          variant="outlined"
          fullWidth
          value={data?.address || ''}
          onChange={e => updateData('address', e.target.value)}
          error={error?.address ? true : false}
          helperText={error?.address || ''}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Número *"
              variant="outlined"
              type="number"
              fullWidth
              value={data?.number || ''}
              onChange={e => updateData('number', e.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              error={error?.number ? true : false}
              helperText={error?.number || ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Complemento"
              variant="outlined"
              fullWidth
              value={data?.complemento || ''}
              onChange={e => updateData('complemento', e.target.value)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              label="Bairro *"
              variant="outlined"
              fullWidth
              value={data?.bairro || ''}
              onChange={e => updateData('bairro', e.target.value)}
              error={error?.bairro ? true : false}
              helperText={error?.bairro || ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Cidade / Estado *"
              variant="outlined"
              fullWidth
              value={data?.cidade || ''}
              onChange={e => updateData('cidade', e.target.value)}
              error={error?.cidade ? true : false}
              helperText={error?.cidade || ''}
            />
          </Grid>
        </Grid>
        <Box display={'flex'} justifyContent={"center"}>
          <LoadingButton loading={isLoadingButton} size='medium' variant="contained" onClick={handleSubmit}>
            Realizar Pagamento
          </LoadingButton>
        </Box>
      </Box>
    </CommonCard>
  )
}
